import {
  findFacilityById,
  findPartnerById,
  findProceduresBySlugs,
  findProfessionalById,
  findProfessionalRatingById,
  findSchedulesByPartnerId,
} from '@/app/(main)/(pages)/_services/search-api/search-api.service';
import { hydrateById, uniqueIds } from '@/app/(main)/(pages)/_services/search-api/hydrate-by-id.util';
import { mapToSearchPartnerData } from '@/app/(main)/(pages)/_services/search-api/map-to-search-partner-data.util';
import { toProcedureFallback } from '@/app/(main)/(pages)/_services/search-api/procedure-fallback.util';
import {
  SearchApiFacility,
  SearchApiPartner,
  SearchApiPartnerProcedureGroup,
  SearchApiPartnerProcedureOption,
  SearchApiProcedure,
  SearchApiProcedurePricing,
  SearchApiProfessional,
  SearchApiRating,
  SearchApiScheduleGroup,
  SearchApiServiceChannel,
} from '@/app/(main)/(pages)/_types/search-api-response.type';
import { SearchPartnerData, SearchType } from '@/app/(main)/(pages)/busca/[slug]/_types/search-partner-data.type';
import { getLogger } from '@/logger/logger';

export const PARTNER_PROCEDURES_PAGE_SIZE = 10;

export interface PartnerProcedureProfessional {
  id: string;
  totalPrice: number;
  schedule: SearchPartnerData;
}

export interface PartnerProcedureGroup {
  id: string;
  name: string;
  professionals: PartnerProcedureProfessional[];
}

export interface GetPartnerProceduresParams {
  partnerId: string;
  serviceChannel: SearchApiServiceChannel;
  page?: number;
  limit?: number;
}

export interface GetPartnerProceduresResult {
  procedures: PartnerProcedureGroup[];
  hasMore: boolean;
}

function toScheduleGroup(
  group: SearchApiPartnerProcedureGroup,
  option: SearchApiPartnerProcedureOption,
): SearchApiScheduleGroup {
  return {
    professionalId: option.professionalId,
    partnerId: option.partnerId,
    partnerFacilityId: option.partnerFacilityId,
    schedulesByScheduleType: option.schedulesByScheduleType,
    procedures: [{ id: group.procedure.id, name: group.procedure.name, slug: group.procedure.slug }],
    distance: null,
    score: 0,
    timeZone: option.timeZone,
    timeOffset: option.timeOffset,
  };
}

function toPricing(
  group: SearchApiPartnerProcedureGroup,
  option: SearchApiPartnerProcedureOption,
): SearchApiProcedurePricing {
  const { promotion } = option;

  return {
    partnerFacilityId: option.partnerFacilityId ?? '',
    procedures: [
      {
        procedureId: group.procedure.id,
        price: option.price ?? 0,
        promotion: promotion
          ? { id: promotion.id, originalPrice: promotion.originalPrice, price: promotion.price }
          : null,
      },
    ],
  };
}

interface BuildProfessionalArgs {
  group: SearchApiPartnerProcedureGroup;
  option: SearchApiPartnerProcedureOption;
  procedure: SearchApiProcedure;
  professional: SearchApiProfessional | null;
  rating: SearchApiRating | null;
  partner: SearchApiPartner | null;
  facility: SearchApiFacility | null;
  searchType?: SearchType;
}

function buildProfessional({
  group,
  option,
  procedure,
  professional,
  rating,
  partner,
  facility,
  searchType,
}: BuildProfessionalArgs): PartnerProcedureProfessional {
  const schedule = mapToSearchPartnerData({
    group: toScheduleGroup(group, option),
    procedures: [procedure],
    professional,
    partner,
    facility,
    rating,
    pricing: toPricing(group, option),
    searchType,
  });

  return {
    id: `${option.professionalId ?? ''}-${option.partnerFacilityId ?? ''}`,
    totalPrice: option.price ?? 0,
    schedule,
  };
}

export function mergePartnerProcedureGroups(
  current: PartnerProcedureGroup[],
  incoming: PartnerProcedureGroup[],
): PartnerProcedureGroup[] {
  const merged = current.map((group) => ({ ...group, professionals: [...group.professionals] }));
  const byId = new Map(merged.map((group) => [group.id, group]));

  incoming.forEach((group) => {
    const existing = byId.get(group.id);

    if (!existing) {
      const copy = { ...group, professionals: [...group.professionals] };
      merged.push(copy);
      byId.set(copy.id, copy);
      return;
    }

    const knownIds = new Set(existing.professionals.map((item) => item.id));
    existing.professionals.push(...group.professionals.filter((item) => !knownIds.has(item.id)));
  });

  return merged;
}

export async function getPartnerProceduresFromSearchApi({
  partnerId,
  serviceChannel,
  page = 1,
  limit = PARTNER_PROCEDURES_PAGE_SIZE,
}: GetPartnerProceduresParams): Promise<GetPartnerProceduresResult> {
  const logger = getLogger('getPartnerProceduresFromSearchApi');

  try {
    const { groups, hasMore } = await findSchedulesByPartnerId(partnerId, { serviceChannel, page, limit });

    if (groups.length === 0) {
      return { procedures: [], hasMore };
    }

    const options = groups.flatMap((group) => (group.options ?? []).map((option) => ({ group, option })));
    const slugs = uniqueIds(groups, (group) => group.procedure.slug);
    const professionalIds = uniqueIds(options, ({ option }) => option.professionalId);
    const facilityIds = uniqueIds(options, ({ option }) => option.partnerFacilityId);

    const proceduresRequest: Promise<SearchApiProcedure[]> = slugs.length > 0
      ? findProceduresBySlugs(slugs).catch(() => [])
      : Promise.resolve([]);

    const [procedures, professionals, ratings, facilities, partner] = await Promise.all([
      proceduresRequest,
      hydrateById(professionalIds, findProfessionalById),
      hydrateById(professionalIds, findProfessionalRatingById),
      hydrateById(facilityIds, findFacilityById),
      findPartnerById(partnerId).catch(() => null),
    ]);

    const procedureBySlug = new Map(procedures.map((procedure) => [procedure.slug, procedure]));
    const searchType = serviceChannel === SearchApiServiceChannel.TELEHEALTH
      ? SearchType.ONLINE_CONSULTATION
      : undefined;

    return {
      procedures: groups
        .map((group) => ({
          id: group.procedure.id,
          name: group.procedure.name,
          professionals: (group.options ?? []).map((option) =>
            buildProfessional({
              group,
              option,
              procedure: procedureBySlug.get(group.procedure.slug) ?? toProcedureFallback(group.procedure),
              professional: professionals.get(option.professionalId ?? '') ?? null,
              rating: ratings.get(option.professionalId ?? '') ?? null,
              partner,
              facility: facilities.get(option.partnerFacilityId ?? '') ?? null,
              searchType,
            }),
          ),
        }))
        .filter((group) => group.professionals.length > 0),
      hasMore,
    };
  } catch (error) {
    logger.error('Failed to fetch partner procedures', { partnerId, serviceChannel, page, error });

    throw error;
  }
}
